import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Wallet, ArrowLeft, ArrowUpRight, ArrowDownLeft, Clock, ShoppingBag, Gift, ChevronRight, Info, TrendingUp, Truck, Package, Copy, ExternalLink, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyAccountPage() {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [cashbackBalance, setCashbackBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalEarned, setTotalEarned] = useState(0);
  const [totalUsed, setTotalUsed] = useState(0);
  const [copiedCode, setCopiedCode] = useState(null);

  // Best-effort carrier tracking URL builder (mirrors backend logic for fallback)
  const buildCarrierUrl = (carrier, code) => {
    if (!carrier || !code) return '';
    const c = carrier.toLowerCase();
    const tpls = [
      ['correios', `https://rastreamento.correios.com.br/app/index.php?objeto=${code}`],
      ['jadlog', `https://www.jadlog.com.br/tracking?cte=${code}`],
      ['loggi', `https://www.loggi.com/rastreador/?code=${code}`],
      ['azul cargo', `https://www.azulcargo.com.br/Rastreio?codigo=${code}`],
      ['total express', `https://tracking.totalexpress.com.br/poupup_track.php?reid=&pedido=${code}`],
      ['j&t', `https://www.jtexpress.com.br/index/query/gzquery.html?bills=${code}`],
      ['shopee envios', `https://spx.com.br/track?id=${code}`],
    ];
    for (const [needle, tpl] of tpls) {
      if (c.includes(needle)) return tpl;
    }
    return '';
  };

  const copyTrackingCode = async (e, code) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  useEffect(() => {
    // Wait for the auth check to finish before deciding to redirect,
    // otherwise a direct load/refresh of this page bounces to /entrar
    // even when the user is logged in.
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/entrar', { state: { from: { pathname: '/minha-conta' } } });
      return;
    }

    // Resilient balance fetch: retry once and never blank the value on a
    // transient error (keeps last known balance instead of showing R$ 0,00).
    const fetchBalance = async (retry = true) => {
      try {
        const res = await axios.get(`${API}/cashback/balance`);
        setCashbackBalance(res.data.balance || 0);
      } catch (e) {
        if (retry) setTimeout(() => fetchBalance(false), 1500);
      }
    };

    const fetchData = async () => {
      setLoading(true);
      // Fetch each resource independently so one failure doesn't wipe the others
      const [historyRes, ordersRes] = await Promise.all([
        axios.get(`${API}/cashback/history`).catch(() => null),
        axios.get(`${API}/orders/my-orders`).catch(() => ({ data: [] })),
      ]);

      await fetchBalance();

      if (historyRes) {
        const history = historyRes.data || [];
        setTransactions(history);
        const earned = history
          .filter(t => t.type === 'credit')
          .reduce((sum, t) => sum + t.amount, 0);
        const used = history
          .filter(t => t.type === 'debit')
          .reduce((sum, t) => sum + t.amount, 0);
        setTotalEarned(earned);
        setTotalUsed(used);
      }
      setOrders(ordersRes.data || []);
      setLoading(false);
    };

    fetchData();
  }, [isAuthenticated, authLoading, navigate]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F8F8]" data-testid="account-loading">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-emerald-600 border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-[#F8F8F8]" data-testid="my-account-page">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link 
            to="/" 
            className="inline-flex items-center text-sm text-gray-600 hover:text-[#0a0a0a] mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Voltar
          </Link>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-['Playfair_Display']">
            Minha Conta
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {user?.name || user?.email}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {loading ? (
          <div className="space-y-4">
            <div className="h-48 bg-white rounded-xl animate-pulse" />
            <div className="h-32 bg-white rounded-xl animate-pulse" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Cashback Balance Card */}
            <div className="bg-gradient-to-br from-emerald-800 via-emerald-900 to-emerald-950 rounded-2xl p-6 md:p-8 text-white shadow-xl" data-testid="cashback-balance-card">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-white/15 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <Wallet className="h-6 w-6 text-emerald-200" />
                </div>
                <div>
                  <p className="text-emerald-200 text-sm font-medium">Seu saldo de cashback</p>
                  <p className="text-3xl md:text-4xl font-bold" data-testid="cashback-balance-value">
                    R$ {cashbackBalance.toFixed(2).replace('.', ',')}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <ArrowDownLeft className="h-4 w-4 text-emerald-300" />
                    <p className="text-emerald-200 text-xs font-medium">Total recebido</p>
                  </div>
                  <p className="text-xl font-bold" data-testid="total-earned">R$ {totalEarned.toFixed(2).replace('.', ',')}</p>
                </div>
                <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <ArrowUpRight className="h-4 w-4 text-amber-300" />
                    <p className="text-emerald-200 text-xs font-medium">Total utilizado</p>
                  </div>
                  <p className="text-xl font-bold" data-testid="total-used">R$ {totalUsed.toFixed(2).replace('.', ',')}</p>
                </div>
              </div>

              {cashbackBalance > 0 && (
                <Link to="/produtos" className="block mt-4">
                  <Button className="w-full bg-white text-emerald-900 hover:bg-emerald-50 rounded-xl py-5 font-semibold" data-testid="use-cashback-btn">
                    <ShoppingBag className="mr-2 h-4 w-4" />
                    Usar meu cashback agora
                  </Button>
                </Link>
              )}
            </div>

            {/* Meus Pedidos */}
            <div className="bg-white rounded-xl shadow-sm p-6" data-testid="my-orders-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Package className="h-5 w-5 text-violet-600" />
                  Meus Pedidos
                </h2>
                <Link to="/rastreio" className="text-xs font-semibold text-violet-700 hover:text-violet-900 inline-flex items-center gap-1" data-testid="my-orders-track-link">
                  Rastrear pedido <ChevronRight className="h-3 w-3" />
                </Link>
              </div>
              {orders.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl">
                  <Truck className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-700">Você ainda não fez pedidos</p>
                  <p className="text-xs text-gray-500 mt-0.5">Suas compras aparecerão aqui com status e rastreio</p>
                  <Link to="/produtos">
                    <Button className="mt-3 bg-[#0a0a0a] hover:bg-black text-white rounded-lg" size="sm">
                      Ver produtos <ChevronRight className="ml-1 h-3 w-3" />
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {orders.slice(0, 5).map((o) => {
                    const hasTracking = Boolean(o.tracking_code);
                    const carrierUrl = o.tracking_url || buildCarrierUrl(o.carrier, o.tracking_code);
                    return (
                      <div key={o.order_id} className="py-3" data-testid={`my-order-${o.order_id}`}>
                        <Link
                          to={`/rastreio?pedido=${encodeURIComponent(o.order_id)}`}
                          className="flex items-center justify-between hover:bg-gray-50 px-2 -mx-2 rounded-lg transition-colors"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-mono text-gray-900 truncate">{o.order_id}</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {o.created_at ? new Date(o.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                              {' · '}{(o.items?.length || 0)} {(o.items?.length || 0) === 1 ? 'item' : 'itens'}
                            </p>
                          </div>
                          <div className="text-right ml-3">
                            <p className="text-sm font-bold text-gray-900">R$ {(o.total || 0).toFixed(2).replace('.', ',')}</p>
                            <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full mt-0.5 ${
                              o.status === 'delivered' ? 'bg-emerald-100 text-emerald-700' :
                              o.status === 'shipped' ? 'bg-violet-100 text-violet-700' :
                              o.status === 'paid' ? 'bg-blue-100 text-blue-700' :
                              'bg-amber-100 text-amber-700'
                            }`}>
                              {o.status === 'delivered' ? 'Entregue' :
                               o.status === 'shipped' ? 'Em transporte' :
                               o.status === 'paid' ? 'Pagamento confirmado' :
                               o.status === 'cancelled' ? 'Cancelado' :
                               o.status === 'processing' ? 'Em preparação' :
                               'Aguardando pagamento'}
                            </span>
                          </div>
                          <ChevronRight className="h-4 w-4 text-gray-400 ml-2" />
                        </Link>

                        {/* Tracking info — appears automatically when admin sets the code */}
                        {hasTracking && (
                          <div className="mt-2 ml-2 -mr-2 bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-200 rounded-xl p-3" data-testid={`my-order-tracking-${o.order_id}`}>
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <div className="min-w-0">
                                <p className="text-[10px] font-bold uppercase tracking-wide text-violet-700 mb-0.5 flex items-center gap-1">
                                  <Truck className="h-3 w-3" /> Código de rastreio
                                  {o.carrier && <span className="text-[10px] font-medium text-gray-600 normal-case">· {o.carrier}</span>}
                                </p>
                                <p className="font-mono text-sm font-bold text-gray-900 break-all">{o.tracking_code}</p>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <button
                                  type="button"
                                  onClick={(e) => copyTrackingCode(e, o.tracking_code)}
                                  className="inline-flex items-center gap-1 text-xs font-semibold text-violet-700 hover:text-violet-900 bg-white border border-violet-200 hover:border-violet-400 rounded-lg px-2.5 py-1.5 transition-colors"
                                  data-testid={`copy-tracking-${o.order_id}`}
                                  aria-label="Copiar código de rastreio"
                                >
                                  {copiedCode === o.tracking_code ? (
                                    <><Check className="h-3 w-3" /> Copiado!</>
                                  ) : (
                                    <><Copy className="h-3 w-3" /> Copiar</>
                                  )}
                                </button>
                                {carrierUrl && (
                                  <a
                                    href={carrierUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-violet-700 hover:bg-violet-800 rounded-lg px-2.5 py-1.5 transition-colors"
                                    data-testid={`open-tracking-${o.order_id}`}
                                  >
                                    Rastrear <ExternalLink className="h-3 w-3" />
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {orders.length > 5 && (
                    <Link to="/rastreio" className="block text-center py-3 text-sm font-semibold text-violet-700 hover:text-violet-900">
                      Ver todos os pedidos →
                    </Link>
                  )}
                </div>
              )}
            </div>

            {/* Como funciona */}
            <div className="bg-white rounded-xl shadow-sm p-6" data-testid="cashback-rules">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Info className="h-5 w-5 text-emerald-600" />
                Como funciona o Cashback
              </h2>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="flex items-start gap-3 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                  <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">1</div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Compre no site</p>
                    <p className="text-xs text-gray-600 mt-0.5">Escolha seus produtos favoritos e finalize a compra</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                  <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">2</div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Ganhe 10% de volta</p>
                    <p className="text-xs text-gray-600 mt-0.5">A cada compra aprovada, 10% do valor volta como cashback</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                  <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">3</div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Use como desconto</p>
                    <p className="text-xs text-gray-600 mt-0.5">Na pr&oacute;xima compra, ative o cashback no carrinho</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Historico de transacoes */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="cashback-history">
              <div className="p-6 border-b">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-gray-500" />
                  Historico de Cashback
                </h2>
              </div>
              
              {transactions.length === 0 ? (
                <div className="p-8 text-center">
                  <Gift className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium">Nenhuma transacao ainda</p>
                  <p className="text-gray-400 text-sm mt-1">Faca sua primeira compra e ganhe 10% de cashback!</p>
                  <Link to="/produtos">
                    <Button className="mt-4 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg" data-testid="start-shopping-btn">
                      Comecar a comprar
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {transactions.map((tx, idx) => (
                    <div key={tx.transaction_id || idx} className="p-4 md:px-6 flex items-center gap-4 hover:bg-gray-50 transition-colors" data-testid={`transaction-${idx}`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        tx.type === 'credit' 
                          ? 'bg-emerald-100' 
                          : 'bg-amber-100'
                      }`}>
                        {tx.type === 'credit' 
                          ? <ArrowDownLeft className="h-5 w-5 text-emerald-600" />
                          : <ArrowUpRight className="h-5 w-5 text-amber-600" />
                        }
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {tx.description || (tx.type === 'credit' ? 'Cashback recebido' : 'Cashback utilizado')}
                        </p>
                        <p className="text-xs text-gray-500">
                          {tx.created_at ? new Date(tx.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }) : ''}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${tx.type === 'credit' ? 'text-emerald-600' : 'text-amber-600'}`}>
                          {tx.type === 'credit' ? '+' : '-'}R$ {tx.amount.toFixed(2).replace('.', ',')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Banner promocional */}
            <div className="bg-gradient-to-r from-[#0a0a0a] to-[#1a1a1a] rounded-2xl p-6 md:p-8 text-white" data-testid="cashback-promo-banner">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="h-8 w-8 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">10% de Cashback em todas as compras!</h3>
                  <p className="text-gray-400 text-sm mt-1">
                    Sem limite. Sem prazo de validade. Acumule e use quando quiser.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
